import { z } from 'zod';
import type {
  CommissioningAggregationLevel, CommissioningAggregationSource, CommissioningBonusMalusRule, CommissioningRankingConfig,
  CommissioningRankingPrize, CommissioningThreshold, CommissioningThresholdBandStructure, CommissioningThresholdDeclaration,
} from '../types/commissioning';
import {
  COMMISSIONING_BONUS_MALUS_EFFECTS, COMMISSIONING_BONUS_MALUS_WINDOWS, COMMISSIONING_RANKING_DIRECTIONS,
  COMMISSIONING_RANKING_METRICS, COMMISSIONING_THRESHOLD_SOURCES,
} from '../types/commissioning';
import { COMMISSIONING_RANKING_TIE_BREAKERS } from '../types/commissioning-runtime-v3';
import {
  aggregationLevelSchema, aggregationSourceSchema, bonusMalusRuleSchema, rankingConfigSchema, rankingPrizeSchema,
  refineAggregationIdentities, refineBonusMalusRuleIdentities, refineCommissioningThresholdStructure,
  refineRankingPrizeIdentities, thresholdBandStructureSchema, thresholdEffectiveBandSchema, thresholdsDeclarationSchema,
} from './commissioning';
import { commissioningThresholdSourceBindingsSchema, commissioningThresholdRunSummarySchema } from './commissioning-threshold-state';
import { commissioningNumericObservationBindingsSchema } from './commissioning-numeric-observations';
import { COMMISSIONING_THRESHOLD_SOURCE_FIELD, commissioningThresholdValuesSchema } from './commissioning-threshold-values';
import { COMMISSIONING_THRESHOLD_PARAMETER_KEYS, type CommissioningThresholdParameterKey } from './commissioning-threshold-economics';

// These schemas refer to the template contract lazily: commissioning.ts also
// contains the instance input schemas which import the runtime variant schema.
// There is one validation implementation, not a second economic contract.
const declaration: z.ZodType<CommissioningThresholdDeclaration> = z.lazy(() => thresholdsDeclarationSchema);
const effectiveBand: z.ZodType<CommissioningThreshold> = z.lazy(() => thresholdEffectiveBandSchema);
const structureBand: z.ZodType<CommissioningThresholdBandStructure> = z.lazy(() => thresholdBandStructureSchema);
const bonusMalusRule: z.ZodType<CommissioningBonusMalusRule> = z.lazy(() => bonusMalusRuleSchema);
const rankingConfig: z.ZodType<CommissioningRankingConfig> = z.lazy(() => rankingConfigSchema);
const rankingPrize: z.ZodType<CommissioningRankingPrize> = z.lazy(() => rankingPrizeSchema);
const aggregationSource: z.ZodType<CommissioningAggregationSource> = z.lazy(() => aggregationSourceSchema);
const aggregationLevel: z.ZodType<CommissioningAggregationLevel> = z.lazy(() => aggregationLevelSchema);
const paymentSources = z.array(z.enum(COMMISSIONING_THRESHOLD_SOURCES)).max(COMMISSIONING_THRESHOLD_SOURCES.length);

/** Riporta gli issue strutturali nel vocabolario della sezione (bands/declaration/paymentSources). */
const reportStructureIssue = (ctx: z.RefinementCtx) => (path: (string | number)[], message: string): void => {
  const [first, ...rest] = path;
  const remapped = first === 'thresholds' ? ['bands', ...rest]
    : first === 'thresholdsDeclaration' ? ['declaration', ...rest]
      : first === 'remunerationDefaults' ? rest : path;
  ctx.addIssue({ code: z.ZodIssueCode.custom, path: remapped, message });
};

/**
 * STRUTTURA Soglie del template come la vedono gli editor di gara: la stessa
 * dichiarazione, le bande (solo identità/nome/ordine/condizioni) e le fonti
 * paganti. È la parte "non modificabile in gara" del contratto.
 */
export const commissioningThresholdStructureSchema = z.object({
  declaration,
  bands: z.array(structureBand).min(1).max(100),
  paymentSources,
}).strict().superRefine((structure, ctx) => {
  refineCommissioningThresholdStructure({
    declaration: structure.declaration, bands: structure.bands, paymentSources: structure.paymentSources,
  }, reportStructureIssue(ctx));
});
export type CommissioningThresholdStructure = z.infer<typeof commissioningThresholdStructureSchema>;

/**
 * STRUTTURA «Bonus/Malus su KPI» del template: regole con identità, KPI,
 * finestra, condizione ed effetti dichiarati. Nessun numero: banda morta e
 * importi sono valori per livello (`values.rules.<id>`), come le bande Soglie.
 */
export const commissioningBonusMalusStructureSchema = z.object({
  rules: z.array(bonusMalusRule).min(1).max(100),
}).strict().superRefine((structure, ctx) => {
  refineBonusMalusRuleIdentities(structure.rules,
    (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rules', ...path], message }));
});
export type CommissioningBonusMalusStructure = z.infer<typeof commissioningBonusMalusStructureSchema>;

/**
 * Regola «Bonus/Malus su KPI» EFFETTIVA: struttura + numeri composti dal
 * livello più specifico (mai salvata; la produce `composeCommissioningBonusMalusRules`).
 */
export const commissioningBonusMalusEffectiveRuleSchema = z.object({
  id: z.string().uuid(),
  label: z.string().max(200).optional(),
  kpiVariableCode: z.string().min(1).max(80),
  window: z.enum(COMMISSIONING_BONUS_MALUS_WINDOWS).optional(),
  functionId: z.string().uuid().optional(),
  effects: z.array(z.enum(COMMISSIONING_BONUS_MALUS_EFFECTS)).min(1),
  deadBandLow: z.number().finite(),
  deadBandHigh: z.number().finite(),
  malusValue: z.number().finite().optional(),
  bonusValue: z.number().finite().optional(),
}).strict().superRefine((rule, ctx) => {
  if (rule.deadBandLow > rule.deadBandHigh) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['deadBandHigh'], message: 'La banda morta «a» deve essere ≥ «da»',
  });
  for (const effect of COMMISSIONING_BONUS_MALUS_EFFECTS) {
    const field = effect === 'malus' ? 'malusValue' : 'bonusValue';
    const declared = rule.effects.includes(effect);
    if (declared && rule[field] === undefined) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: [field], message: 'Importo mancante per un effetto dichiarato',
    });
    if (!declared && rule[field] !== undefined) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: [field], message: 'Importo presente per un effetto non dichiarato',
    });
  }
});
export type CommissioningBonusMalusEffectiveRule = z.infer<typeof commissioningBonusMalusEffectiveRuleSchema>;

/**
 * STRUTTURA Classifica del template: criteri (metrica, verso, spareggio) e
 * fasce di posizione con identità. Nessun numero: premi e partecipanti
 * minimi sono valori per livello (`values.prizes.<id>`, `values.ranking`).
 */
export const commissioningRankingStructureSchema = z.object({
  config: rankingConfig,
  prizes: z.array(rankingPrize).min(1).max(200),
}).strict().superRefine((structure, ctx) => {
  refineRankingPrizeIdentities(structure.prizes,
    (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['prizes', ...path], message }));
});
export type CommissioningRankingStructure = z.infer<typeof commissioningRankingStructureSchema>;

/** Classifica EFFETTIVA: struttura + numeri composti (mai salvata). */
export const commissioningRankingEffectiveSchema = z.object({
  metric: z.enum(COMMISSIONING_RANKING_METRICS),
  metricVariableCode: z.string().min(1).max(80).optional(),
  direction: z.enum(COMMISSIONING_RANKING_DIRECTIONS),
  tieBreaker: z.enum(COMMISSIONING_RANKING_TIE_BREAKERS),
  minParticipants: z.number().int().min(1).optional(),
  prizes: z.array(z.object({
    id: z.string().uuid(), label: z.string().max(200).optional(),
    fromPosition: z.number().int().min(1), toPosition: z.number().int().min(1).optional(),
    prizeValue: z.number().finite(),
  }).strict()).min(1),
}).strict();
export type CommissioningRankingEffective = z.infer<typeof commissioningRankingEffectiveSchema>;

/**
 * STRUTTURA Aggregazione del template: sorgenti ordinate (template) e livelli
 * con nome. Nessun numero: pesi, punteggi minimi e payout sono valori per
 * livello (`values.sources.<id>`, `values.levels.<id>`).
 */
export const commissioningAggregationStructureSchema = z.object({
  sources: z.array(aggregationSource).min(1).max(100),
  levels: z.array(aggregationLevel).min(1).max(100),
}).strict().superRefine((structure, ctx) => {
  refineAggregationIdentities(structure.sources, structure.levels, (path, message) => {
    const [first, ...rest] = path;
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [first === 'aggregationSources' ? 'sources' : 'levels', ...rest], message });
  });
});
export type CommissioningAggregationStructure = z.infer<typeof commissioningAggregationStructureSchema>;

/** Aggregazione EFFETTIVA: struttura + numeri composti (mai salvata). */
export const commissioningAggregationEffectiveSchema = z.object({
  sources: z.array(z.object({
    id: z.string().uuid(), templateId: z.string().uuid(), label: z.string().max(200).optional(),
    weight: z.number().finite().nonnegative(),
  }).strict()).min(1),
  levels: z.array(z.object({
    id: z.string().uuid(), level: z.string().min(1).max(80),
    minScore: z.number().finite(), payout: z.number().finite(),
  }).strict()).min(1),
}).strict();
export type CommissioningAggregationEffective = z.infer<typeof commissioningAggregationEffectiveSchema>;

/**
 * Struttura a valori di un template, per tipo: Soglie (bande), Bonus/Malus
 * su KPI (regole), Classifica (fasce) oppure Aggregazione (sorgenti e
 * livelli). È ciò che l'editor «Valori per cluster», lo stato per
 * assegnazione e il preflight condividono.
 */
export const commissioningValuesStructureSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('soglie'), structure: commissioningThresholdStructureSchema }).strict(),
  z.object({ kind: z.literal('bonus_malus'), structure: commissioningBonusMalusStructureSchema }).strict(),
  z.object({ kind: z.literal('classifica'), structure: commissioningRankingStructureSchema }).strict(),
  z.object({ kind: z.literal('aggregazione'), structure: commissioningAggregationStructureSchema }).strict(),
]);
export type CommissioningValuesStructure = z.infer<typeof commissioningValuesStructureSchema>;

/**
 * Sezione Soglie EFFETTIVA: struttura del template composta con i valori del
 * livello più specifico. È la forma che il motore consuma; non è mai salvata
 * (la producono `composeCommissioningThresholdSection` e il resolver).
 */
export const commissioningDeclarativeThresholdSectionSchema = z.object({
  declaration,
  bands: z.array(effectiveBand).min(1).max(100),
  paymentSources,
  sourceBindings: commissioningThresholdSourceBindingsSchema.optional(),
  observationBindings: commissioningNumericObservationBindingsSchema.optional(),
}).strict().superRefine((section, ctx) => {
  refineCommissioningThresholdStructure({
    declaration: section.declaration,
    bands: section.bands.map((band) => ({
      id: band.id, name: band.name,
      ...(band.categories ? { categories: band.categories.map((rule) => ({
        id: rule.id, name: rule.name, priority: rule.priority, functionId: rule.functionId,
        parameters: parameterKeys(rule.parameters),
      })) } : {}),
      ...(band.surcharges ? { surcharges: band.surcharges.map((rule) => ({
        id: rule.id, name: rule.name, functionId: rule.functionId, delta: parameterKeys(rule.delta),
      })) } : {}),
      ...(band.admission ? { admission: band.admission } : {}),
    })),
    paymentSources: section.paymentSources,
  }, reportStructureIssue(ctx));
  const declaredFields = new Set(section.declaration.sources.map((s) => COMMISSIONING_THRESHOLD_SOURCE_FIELD[s]));
  const payingFields = new Set(section.paymentSources.map((s) => COMMISSIONING_THRESHOLD_SOURCE_FIELD[s]));
  section.bands.forEach((band, index) => {
    for (const field of Object.values(COMMISSIONING_THRESHOLD_SOURCE_FIELD)) {
      if (band[field] !== undefined && !declaredFields.has(field)) ctx.addIssue({
        code: z.ZodIssueCode.custom, path: ['bands', index, field], message: `Banda ${index + 1}: la fonte "${field}" non è dichiarata`,
      });
      if (band[field] === undefined && payingFields.has(field)) ctx.addIssue({
        code: z.ZodIssueCode.custom, path: ['bands', index, field], message: `Banda ${index + 1}: valore mancante per una fonte pagante`,
      });
    }
    if (band.to !== undefined && band.to <= band.from) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['bands', index, 'to'], message: `Banda ${index + 1}: "a" deve essere > "da"`,
    });
    if (band.to === undefined && index < section.bands.length - 1) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['bands', index, 'to'], message: `Banda ${index + 1}: solo l’ultima banda può essere aperta`,
    });
    const previous = section.bands[index - 1];
    if (previous && previous.to !== undefined && band.from < previous.to) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['bands', index, 'from'], message: `Banda ${index + 1}: si sovrappone alla banda precedente`,
    });
  });
});
export type CommissioningDeclarativeThresholdSection = z.infer<typeof commissioningDeclarativeThresholdSectionSchema>;

function parameterKeys(parameters: Record<string, number | undefined>): CommissioningThresholdParameterKey[] {
  const keys: CommissioningThresholdParameterKey[] = [];
  for (const key of COMMISSIONING_THRESHOLD_PARAMETER_KEYS) {
    if (parameters[key] !== undefined) keys.push(key);
  }
  return keys;
}

/**
 * VALORI Soglie salvati a un livello (istanza, cluster, cella driver, membro)
 * nei contenitori esistenti `aggregate_rules.declarativeThresholds`,
 * `rule_variant.declarativeThresholds`, `rule_variant.memberThresholds[id]`.
 * I binding sorgenti/osservazioni restano accanto ai valori del livello.
 */
export const commissioningThresholdLevelValuesSchema = z.object({
  values: commissioningThresholdValuesSchema,
  sourceBindings: commissioningThresholdSourceBindingsSchema.optional(),
  observationBindings: commissioningNumericObservationBindingsSchema.optional(),
}).strict();
export type CommissioningThresholdLevelValues = z.infer<typeof commissioningThresholdLevelValuesSchema>;

export const commissioningMemberThresholdsSchema = z.record(
  z.string().uuid(), commissioningThresholdLevelValuesSchema,
).superRefine((members, ctx) => {
  if (Object.keys(members).length > 5000) ctx.addIssue({
    code: z.ZodIssueCode.custom, message: 'Sono ammessi al massimo 5000 membri per assegnazione',
  });
});
export type CommissioningMemberThresholds = z.infer<typeof commissioningMemberThresholdsSchema>;

export const commissioningThresholdEditLevelSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('instance') }).strict(),
  z.object({ kind: z.literal('assignment'), assignmentId: z.string().uuid(), driverId: z.string().uuid().optional() }).strict(),
  z.object({ kind: z.literal('driver'), assignmentId: z.string().uuid(), driverId: z.string().uuid() }).strict(),
  z.object({ kind: z.literal('member'), assignmentId: z.string().uuid(), memberId: z.string().uuid(), driverId: z.string().uuid().optional() }).strict(),
]);
export type CommissioningThresholdEditLevel = z.infer<typeof commissioningThresholdEditLevelSchema>;

export const updateCommissioningDeclarativeThresholdsSchema = z.object({
  level: commissioningThresholdEditLevelSchema,
  /** Opaque revision of the editing context; never parse it as a Date. */
  revision: z.string().min(1).max(100),
  /** Valori del livello; null = azzera il livello (torna a ereditare tutto). */
  section: commissioningThresholdLevelValuesSchema.nullable(),
  recalc_ack: z.boolean().optional(),
}).strict();
export type UpdateCommissioningDeclarativeThresholdsInput = z.infer<typeof updateCommissioningDeclarativeThresholdsSchema>;

export const commissioningThresholdImportChangeSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('replace'), memberId: z.string().uuid(),
    values: commissioningThresholdLevelValuesSchema,
  }).strict(),
  z.object({ action: z.literal('reset'), memberId: z.string().uuid() }).strict(),
]);
export const previewCommissioningThresholdImportSchema = z.object({
  assignmentId: z.string().uuid(),
  changes: z.array(commissioningThresholdImportChangeSchema).min(1).max(5000),
}).strict().superRefine((input, ctx) => {
  const seen = new Set<string>();
  input.changes.forEach((change, index) => {
    if (seen.has(change.memberId)) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['changes', index, 'memberId'],
      message: 'Membro ripetuto: ogni membro può comparire una sola volta',
    });
    seen.add(change.memberId);
  });
});
export const applyCommissioningThresholdImportSchema = z.object({
  assignmentId: z.string().uuid(),
  changes: z.array(commissioningThresholdImportChangeSchema).min(1).max(5000),
  revision: z.string().min(1).max(100),
  membershipFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  recalc_ack: z.boolean().optional(),
}).strict().superRefine((input, ctx) => {
  const check = previewCommissioningThresholdImportSchema.safeParse({
    assignmentId: input.assignmentId, changes: input.changes,
  });
  if (!check.success) for (const issue of check.error.issues) ctx.addIssue(issue);
});
export type PreviewCommissioningThresholdImportInput = z.infer<typeof previewCommissioningThresholdImportSchema>;
export type ApplyCommissioningThresholdImportInput = z.infer<typeof applyCommissioningThresholdImportSchema>;

export const commissioningThresholdOriginSchema = z.enum(['template', 'instance', 'assignment', 'driver', 'member']);
export type CommissioningThresholdOrigin = z.infer<typeof commissioningThresholdOriginSchema>;

/**
 * Valore richiesto dalla struttura e non presente nella composizione.
 * Esattamente una identità: `bandId` (banda Soglie), `ruleId` (regola
 * Bonus/Malus su KPI), `prizeId` (fascia Classifica), `sourceId` (sorgente
 * Aggregazione) oppure `levelId` (livello Aggregazione).
 */
export const commissioningThresholdMissingValueSchema = z.object({
  bandId: z.string().uuid().optional(),
  ruleId: z.string().uuid().optional(),
  prizeId: z.string().uuid().optional(),
  sourceId: z.string().uuid().optional(),
  levelId: z.string().uuid().optional(),
  /** Percorso stabile del valore (`bands.<id>.from`, `rules.<id>.deadBandLow`, `prizes.<id>.prizeValue`, `sources.<id>.weight`, `levels.<id>.payout`). */
  path: z.string().min(1),
  label: z.string().min(1),
}).strict().refine((missing) => [missing.bandId, missing.ruleId, missing.prizeId, missing.sourceId, missing.levelId]
  .filter((id) => id !== undefined).length === 1, {
  message: 'Un valore mancante appartiene a una sola identità della struttura',
});
export type CommissioningThresholdMissingValue = z.infer<typeof commissioningThresholdMissingValueSchema>;

export const commissioningThresholdCompletenessSchema = z.object({
  complete: z.boolean(),
  missing: z.array(commissioningThresholdMissingValueSchema),
  /** Incoerenze tra valori composti (A ≤ Da, sovrapposizioni). */
  issues: z.array(z.object({ path: z.string(), message: z.string() }).strict()),
}).strict();
export type CommissioningThresholdCompleteness = z.infer<typeof commissioningThresholdCompletenessSchema>;

/** Strato di valori con la sua origine (ordinati dal meno al più specifico). */
export const commissioningThresholdValueLayerSchema = z.object({
  origin: commissioningThresholdOriginSchema,
  values: commissioningThresholdValuesSchema,
}).strict();
export type CommissioningThresholdValueLayer = z.infer<typeof commissioningThresholdValueLayerSchema>;

export const commissioningThresholdEditorContextSchema = z.object({
  instanceId: z.string().uuid(), level: commissioningThresholdEditLevelSchema,
  revision: z.string().min(1), activeCompetition: z.boolean(),
  competitionId: z.string().uuid(),
  /** Struttura a valori del template (sola lettura in gara); null = il template non ne dichiara una. */
  structure: commissioningValuesStructureSchema.nullable(),
  /** Strati EREDITATI dal livello corrente (senza i valori locali), dal meno al più specifico. */
  inheritedLayers: z.array(commissioningThresholdValueLayerSchema),
  /** Valori salvati al livello corrente. */
  local: commissioningThresholdLevelValuesSchema.nullable(),
  /** Composizione effettiva (struttura + strati ereditati + locale); null se incompleta o senza struttura. */
  effective: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('soglie'), section: commissioningDeclarativeThresholdSectionSchema }).strict(),
    z.object({ kind: z.literal('bonus_malus'), rules: z.array(commissioningBonusMalusEffectiveRuleSchema).min(1) }).strict(),
    z.object({ kind: z.literal('classifica'), ranking: commissioningRankingEffectiveSchema }).strict(),
    z.object({ kind: z.literal('aggregazione'), aggregation: commissioningAggregationEffectiveSchema }).strict(),
  ]).nullable(),
  /** Origine più specifica che contribuisce almeno un valore. */
  origin: commissioningThresholdOriginSchema.nullable(),
  completeness: commissioningThresholdCompletenessSchema,
  recalculationStatus: commissioningThresholdRunSummarySchema.optional(),
});
export type CommissioningThresholdEditorContext = z.infer<typeof commissioningThresholdEditorContextSchema>;

export const commissioningThresholdImportPreviewSchema = z.object({
  assignmentId: z.string().uuid(), revision: z.string().min(1),
  membershipFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  activeCompetition: z.boolean(),
  rows: z.array(z.object({
    memberId: z.string().uuid(), action: z.enum(['add', 'replace', 'reset']),
    previous: commissioningThresholdLevelValuesSchema.nullable(),
    values: commissioningThresholdLevelValuesSchema.nullable(),
  })),
});
export type CommissioningThresholdImportPreview = z.infer<typeof commissioningThresholdImportPreviewSchema>;

export const commissioningThresholdMutationResultSchema = z.object({
  saved: z.literal(true),
  recalc: z.enum(['not_required', 'queued', 'pending']),
  message: z.string(),
  competitionId: z.string().uuid(),
  recalcRunIds: z.array(z.string().uuid()).optional(),
});
export type CommissioningThresholdMutationResult = z.infer<typeof commissioningThresholdMutationResultSchema>;

/** Stato dei valori Soglie di un abbinamento configuratore × cluster. */
export const commissioningThresholdValuesStatusSchema = z.enum(['complete', 'incomplete', 'not_applicable']);
export type CommissioningThresholdValuesStatus = z.infer<typeof commissioningThresholdValuesStatusSchema>;

/** Optional on legacy instance reads; authenticated Soglie detail supplies the
 * editor token. Mutation outcomes distinguish a committed save from queue delivery. */
export const commissioningInstanceThresholdMetadataSchema = z.object({
  threshold_revision: z.string().min(1).optional(),
  threshold_change: commissioningThresholdMutationResultSchema.optional(),
}).strict();
export type CommissioningInstanceThresholdMetadata = z.infer<typeof commissioningInstanceThresholdMetadataSchema>;
